import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFrameworksApi, api, configApi, controlMatrixApi, departmentsApi, evidenceApi, evidenceRegistersApi, evidenceTemplatesApi, linkedArtefactsApi, raciApi, registerConfigApi, templateApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { toneFromMap, type Tone } from '../constants/tone';

// ── Evidence maps: exported so they remain valid TS and can be used for admin/testing.
// Primary data source is now DynamoDB (dev-nimbus-evidence-templates) via evidenceTemplatesApi.
export const ISO27001_EVIDENCE_MAP: Record<string, string[]> = {
  // ── ISMS Clauses 4–10 ────────────────────────────────────────────────────
  'C.4.1':   ['Context analysis document (internal and external issues)', 'Evidence of periodic review of context analysis', 'Meeting minutes or workshop records from context review'],
  'C.4.2':   ['Interested parties register', 'Documented security requirements per stakeholder', 'Evidence of periodic stakeholder requirements review'],
  'C.4.3':   ['Approved ISMS scope statement signed by management', 'Scope boundary diagram or description', 'Justification for any exclusions from scope'],
  'C.4.4':   ['Documented ISMS processes', 'Process interaction or flow documentation', 'Evidence ISMS is maintained and reviewed'],
  'C.5.1':   ['Management review minutes demonstrating ISMS oversight', 'Evidence of top management sign-off on ISMS policy', 'Resource allocation records for ISMS activities'],
  'C.5.2':   ['Approved information security policy', 'Evidence policy is communicated to all staff', 'Policy review and version history'],
  'C.5.3':   ['Documented ISMS roles and responsibilities', 'Appointment record of ISMS responsible person', 'Evidence responsibilities have been communicated to relevant staff'],
  'C.6.1':   ['Risk management methodology document', 'Documented register of risks and opportunities', 'Evidence of planned actions to address identified risks'],
  'C.6.1.1': ['Risk management methodology document', 'Evidence risks and opportunities to the ISMS are identified', 'Evidence actions are planned and assigned', 'Evidence actions are reviewed for effectiveness'],
  'C.6.1.2': ['Risk assessment criteria and acceptance criteria document', 'Current risk register with assessment results', 'Risk assessment records showing methodology applied'],
  'C.6.1.3': ['Risk treatment plan', 'Statement of Applicability (SoA)', 'Evidence selected controls are implemented'],
  'C.6.2':   ['Documented information security objectives', 'Objectives with assigned owners and target dates', 'KPI or progress tracking records against objectives'],
  'C.6.3':   ['Change planning records affecting the ISMS', 'Impact assessments for ISMS-affecting changes', 'Evidence changes are reviewed before implementation'],
  'C.7.1':   ['Evidence of adequate resourcing for the ISMS', 'Budget or resource allocation records for ISMS activities'],
  'C.7.2':   ['Defined competence requirements for ISMS-related roles', 'Training and qualification records for relevant personnel', 'Evidence of competence evaluation'],
  'C.7.3':   ['Security awareness programme description', 'Awareness training completion records', 'Examples of awareness content delivered'],
  'C.7.4':   ['Internal and external communication plan for the ISMS', 'Evidence communications have been performed'],
  'C.7.5.1': ['ISMS document inventory or register', 'Core documented information items present and approved', 'Evidence documented information is reviewed and kept current'],
  'C.7.5.2': ['Document control procedure', 'Document templates with version and approval fields', 'Sample documents showing version history'],
  'C.7.5.3': ['Document retention policy', 'Access controls applied to ISMS documents', 'Evidence obsolete documents are removed or marked'],
  'C.8.1':   ['Documented operational procedures supporting the ISMS', 'Evidence procedures are followed (records or logs)', 'Operational monitoring records'],
  'C.8.2':   ['Periodic risk assessment reports', 'Evidence new or changed risks are assessed', 'Risk assessment schedule or trigger criteria'],
  'C.8.3':   ['Updated risk treatment actions', 'Residual risk acceptance records', 'Evidence treatment controls remain effective'],
  'C.9.1':   ['Defined monitoring metrics and measurement approach', 'Performance measurement reports', 'Evidence of regular review of measurements'],
  'C.9.2':   ['Internal audit programme and schedule', 'Internal audit reports', 'Evidence audit findings are tracked and resolved'],
  'C.9.3':   ['Management review meeting minutes', 'Management review inputs and outputs documented', 'Management review action items and follow-up evidence'],
  'C.9.3.1': ['Management review meeting minutes', 'Evidence management review covers required inputs'],
  'C.9.3.2': ['Performance and measurement reports presented at review', 'Audit results presented at review', 'Risk status and treatment updates'],
  'C.9.3.3': ['Management review output decisions and action items', 'Evidence of follow-up on management review decisions'],
  'C.10.1':  ['Improvement log or register', 'Evidence of implemented improvements', 'Linkage between improvement initiatives and ISMS objectives'],
  'C.10.2':  ['Corrective action records', 'Root cause analysis documentation', 'Evidence of corrective action effectiveness verification'],
  // ── Annex A Controls ─────────────────────────────────────────────────────
  'A.5.1':   ['Approved information security policy document', 'Evidence of top management approval', 'Policy communication evidence', 'Policy review/version history'],
  'A.5.2':   ['Documented security roles and responsibilities', 'Security governance structure diagram', 'Appointment record of ISMS-responsible person'],
  'A.5.3':   ['Segregation of duties matrix', 'Access configuration evidence showing role separation', 'Periodic review records for conflicting role assignments'],
  'A.5.4':   ['Policy clause requiring staff compliance with security requirements', 'Training completion tracking records', 'Documented disciplinary procedure referencing security breaches'],
  'A.5.5':   ['Procedure for contacting relevant authorities', 'List of relevant regulatory and legal authorities', 'Evidence of contact when required'],
  'A.5.6':   ['Membership or subscription evidence (e.g. ISAC, NCSC feeds)', 'Evidence of monitoring security advisories or bulletins'],
  'A.5.7':   ['Active threat intelligence sources or subscriptions', 'Threat monitoring or advisory review records', 'Evidence threat intelligence informs risk register updates'],
  'A.5.8':   ['Project methodology documents including security requirements', 'Security checklist or risk assessment in project initiation records', 'Security sign-off evidence in project closure records'],
  'A.5.9':   ['Asset register', 'Asset ownership assignments', 'Asset classification records', 'Periodic asset register review evidence'],
  'A.5.10':  ['Acceptable use policy', 'Signed user acknowledgements or acceptance records', 'Evidence policy is communicated to all users'],
  'A.5.11':  ['Offboarding procedure including asset return steps', 'Leaver checklist with asset return confirmation', 'Sample completed termination records showing asset return'],
  'A.5.12':  ['Information classification policy', 'Defined classification levels and handling requirements', 'Examples of classified information in practice'],
  'A.5.13':  ['Labelling procedure', 'Examples of labelled documents or data assets', 'Evidence labelling is applied consistently'],
  'A.5.14':  ['Secure information transfer procedure', 'Encryption requirements for external data transfers', 'Secure file transfer configuration or tool evidence'],
  'A.5.15':  ['Access control policy', 'Role-based access model documentation', 'Evidence of least privilege principle in access configuration'],
  'A.5.16':  ['User account provisioning procedure', 'Unique user ID enforcement evidence', 'Onboarding and offboarding access records', 'Periodic identity and account review records'],
  'A.5.17':  ['Authentication policy (password and MFA requirements)', 'Password hashing and storage configuration evidence', 'TLS enforcement configuration for authentication traffic', 'Secure storage evidence for API keys and secrets'],
  'A.5.18':  ['Access request and approval workflow records', 'RBAC or permission matrix', 'Access review records', 'Termination access removal evidence'],
  'A.5.19':  ['Supplier security requirements in contracts', 'Supplier risk assessment records', 'Evidence security requirements are followed up with key suppliers'],
  'A.5.20':  ['Standard contractual security clauses', 'Data processing agreements (DPAs) where applicable'],
  'A.5.21':  ['ICT supplier due diligence process and records', 'Monitoring records for critical ICT suppliers'],
  'A.5.22':  ['Supplier review records', 'Documented supplier review schedule', 'Evidence of action taken from supplier reviews'],
  'A.5.23':  ['Cloud security policy or guidelines', 'Cloud provider security assurance documentation', 'Cloud risk assessment records'],
  'A.5.24':  ['Incident response plan', 'Documented incident response roles and responsibilities', 'Communication and escalation plan'],
  'A.5.25':  ['Incident triage procedure', 'Severity classification matrix', 'Sample incident triage and assessment records'],
  'A.5.26':  ['Incident response records', 'Containment actions evidence', 'Remediation and closure records'],
  'A.5.27':  ['Post-incident review reports', 'Corrective actions raised from incident reviews', 'Evidence lessons learned are applied and tracked'],
  'A.5.28':  ['Forensic evidence handling procedure', 'Chain of custody documentation template', 'Evidence of procedure being followed in incidents'],
  'A.5.29':  ['Business continuity plan', 'Disaster recovery plan', 'BCP or DR test results and evidence'],
  'A.5.30':  ['Backup policy', 'Backup configuration evidence', 'Backup restoration test results', 'Defined RTO and RPO targets'],
  'A.5.31':  ['Legal and regulatory obligations register', 'Compliance review or assessment evidence', 'Contractual security requirements register'],
  'A.5.32':  ['IP protection policy or procedure', 'Software licence register', 'Evidence licence compliance is monitored'],
  'A.5.33':  ['Records retention policy', 'Retention schedule with defined retention periods', 'Evidence records are protected from loss or tampering'],
  'A.5.34':  ['Privacy policy', 'Records of processing activities (RoPA)', 'DPIA process and completed DPIA records where applicable'],
  'A.5.35':  ['Internal audit programme and reports', 'External or independent audit reports where applicable', 'Evidence audit findings are reported to management'],
  'A.5.36':  ['Compliance review or self-assessment records', 'Corrective action records for identified non-conformities'],
  'A.5.37':  ['Documented operational procedures', 'Version control and approval evidence for procedures', 'Evidence procedures are accessible to relevant staff'],
  'A.6.1':   ['Background screening policy', 'Sample screening records (appropriately anonymised)', 'Evidence screening is applied consistently to defined roles'],
  'A.6.2':   ['Employment contracts containing security obligations', 'Signed acknowledgement of security responsibilities'],
  'A.6.3':   ['Security awareness programme documentation', 'Training completion records', 'Examples of awareness materials delivered'],
  'A.6.4':   ['Documented disciplinary procedure referencing security violations', 'Evidence procedure is communicated to staff'],
  'A.6.5':   ['Offboarding procedure with security steps', 'Termination checklist with access revocation evidence', 'Sample completed offboarding records'],
  'A.6.6':   ['NDA or confidentiality agreement template', 'Signed NDA samples from staff and relevant third parties'],
  'A.6.7':   ['Remote working policy', 'Secure remote access configuration evidence (VPN or equivalent)', 'Evidence remote working controls are enforced'],
  'A.6.8':   ['Security event reporting procedure', 'Evidence reporting channels are communicated to all staff', 'Sample reported security event records'],
  'A.7.1':   ['Physical security policy', 'Site access control evidence (card reader config or door logs)'],
  'A.7.2':   ['Access badge or entry control system evidence', 'Visitor register or logs', 'Evidence visitor access is controlled and escorted'],
  'A.7.3':   ['Office and facility security measures documentation', 'Physical security inspection records'],
  'A.7.4':   ['CCTV or physical monitoring system configuration evidence', 'Evidence monitoring footage or alerts are reviewed'],
  'A.7.5':   ['Fire detection and suppression system evidence', 'Environmental monitoring and alert records'],
  'A.7.6':   ['Secure area access restriction records', 'Working in secure areas procedure'],
  'A.7.7':   ['Clear desk and screen policy', 'Workplace inspection records'],
  'A.7.8':   ['Equipment placement and protection documentation', 'Evidence equipment is protected from environmental hazards'],
  'A.7.9':   ['Remote asset use and transport procedure', 'Records of assets authorised for off-premises use'],
  'A.7.10':  ['Media handling and classification procedure', 'Secure media disposal or destruction evidence'],
  'A.7.11':  ['UPS and power protection documentation', 'Evidence of utility maintenance and testing'],
  'A.7.12':  ['Cabling protection and labelling documentation', 'Physical cabling inspection records'],
  'A.7.13':  ['Equipment maintenance schedule', 'Maintenance logs showing scheduled and unscheduled maintenance'],
  'A.7.14':  ['Secure disposal and sanitisation procedure', 'Disposal records or certificates of destruction'],
  'A.8.1':   ['Endpoint security policy', 'Endpoint protection (AV or MDM) configuration evidence', 'Patch and update status evidence for endpoints'],
  'A.8.2':   ['List of privileged accounts', 'Privileged access request and approval records', 'Periodic privileged access review records'],
  'A.8.3':   ['Permission configuration evidence (application or file share)', 'Network access restriction evidence (firewall rules or ACLs)', 'Access control review records'],
  'A.8.4':   ['Repository access controls configuration', 'Branch protection rules configuration', 'Repository access review records'],
  'A.8.5':   ['MFA configuration evidence', 'Password policy enforcement evidence', 'Session timeout configuration evidence'],
  'A.8.6':   ['Capacity monitoring records and dashboards', 'Capacity threshold alerts configuration', 'Evidence of capacity reviews and planning'],
  'A.8.7':   ['Anti-malware deployment and configuration evidence', 'Malware scan or monitoring logs', 'Evidence of user awareness on malware risks'],
  'A.8.8':   ['Vulnerability scanning reports', 'Patch management procedure', 'Patch application and remediation tracking records', 'Vulnerability remediation SLA evidence'],
  'A.8.9':   ['Configuration baseline documentation', 'Evidence of configuration standards applied to systems', 'Configuration review or deviation records'],
  'A.8.10':  ['Secure deletion procedure', 'Evidence of deletion when required'],
  'A.8.11':  ['Data masking policy or procedure', 'Evidence data masking is applied in non-production environments'],
  'A.8.12':  ['DLP policy', 'DLP tool configuration evidence', 'DLP alert or incident records'],
  'A.8.13':  ['Backup policy', 'Backup configuration evidence', 'Backup restoration test results'],
  'A.8.14':  ['Redundancy and failover design documentation', 'Failover or high-availability test evidence'],
  'A.8.15':  ['Logging policy or requirements', 'Logging configuration evidence across key systems', 'Log review or SIEM alert records'],
  'A.8.16':  ['Monitoring procedure', 'Monitoring tool configuration evidence', 'Alert triage and review records'],
  'A.8.17':  ['NTP configuration evidence', 'Evidence all in-scope systems synchronise to an authoritative time source'],
  'A.8.18':  ['Policy restricting use of privileged utility programs', 'Access control configuration showing restricted access'],
  'A.8.19':  ['Software installation approval policy', 'Evidence of technical enforcement (e.g. application whitelisting or MDM)'],
  'A.8.20':  ['Network security policy', 'Network diagram', 'Firewall rule set and configuration evidence'],
  'A.8.21':  ['Network service security requirements documentation', 'SLA or security terms from network service providers'],
  'A.8.22':  ['Network segmentation design documentation', 'Firewall or VLAN configuration evidence'],
  'A.8.23':  ['Web filtering policy', 'Web filtering tool configuration evidence'],
  'A.8.24':  ['Cryptography policy', 'Encryption at rest configuration evidence', 'TLS or encryption in transit configuration evidence', 'Key management procedure and key rotation evidence'],
  'A.8.25':  ['Secure development policy or SDLC documentation', 'Evidence security requirements are embedded in development stages'],
  'A.8.26':  ['Security requirements documented for applications', 'Evidence security requirements are reviewed in project or design phase'],
  'A.8.27':  ['Security architecture standards or principles documentation', 'Architecture review records for new or changed systems'],
  'A.8.28':  ['Secure coding standards documentation', 'Code review records or evidence'],
  'A.8.29':  ['Security testing policy or procedure', 'Security test reports (SAST, DAST or penetration test)', 'Evidence findings are tracked to resolution'],
  'A.8.30':  ['Security clauses in development contracts', 'Evidence of security review of outsourced development output'],
  'A.8.31':  ['Environment separation policy', 'Evidence of dev/test/prod separation in configuration'],
  'A.8.32':  ['Change management procedure', 'Change request and approval records', 'Change log and post-implementation review records'],
  'A.8.33':  ['Test data handling procedure', 'Evidence production data is not used in testing (or formal approval where it is)'],
  'A.8.34':  ['Audit coordination procedure', 'Evidence of restricted or monitored access during audit activities'],
};

export const SOC2_EVIDENCE_MAP: Record<string, string[]> = {
  // CC1 – Control Environment
  'CC1.1':  ['Board or executive oversight charter', 'Documented commitment to integrity and ethical values', 'Ethics and code of conduct policy'],
  'CC1.2':  ['Organisational chart with reporting lines', 'Defined roles and responsibilities for security governance', 'Board or audit committee meeting minutes'],
  'CC1.3':  ['Competency requirements for key security roles', 'Training and qualification records for security personnel', 'HR policy covering background checks and onboarding'],
  'CC1.4':  ['Performance review records with security KPIs', 'Accountability framework documentation', 'Corrective action records for performance non-conformities'],
  'CC1.5':  ['Succession planning records for critical security roles', 'Retention and incentive policy documentation', 'Evidence security staffing levels are reviewed'],
  // CC2 – Communication and Information
  'CC2.1':  ['Internal security policy communication evidence', 'Intranet or communication records for policy distribution', 'Security awareness training completion records'],
  'CC2.2':  ['External-facing privacy and security commitment statements', 'Customer-facing terms of service or SLA with security terms', 'Data protection / security assurance documentation published externally'],
  'CC2.3':  ['Regulatory and supervisory communication records', 'Breach or incident notification records where required', 'Audit engagement and communication records'],
  // CC3 – Risk Assessment
  'CC3.1':  ['Defined risk assessment objectives and criteria', 'Risk management policy', 'Evidence risk assessment cycle is planned and executed'],
  'CC3.2':  ['Risk register with identified risks to service commitments', 'Risk identification methodology documentation', 'Records of threat and vulnerability identification activities'],
  'CC3.3':  ['Risk analysis and scoring records', 'Risk treatment decisions and residual risk acceptance', 'Risk treatment plan with assigned owners'],
  'CC3.4':  ['Change-triggered risk assessment records', 'Fraud risk assessment documentation', 'Evidence emerging risks are identified and assessed'],
  // CC4 – Monitoring Activities
  'CC4.1':  ['Continuous monitoring programme documentation', 'Monitoring metrics and dashboards', 'Evidence of ongoing control effectiveness monitoring'],
  'CC4.2':  ['Deficiency tracking and escalation records', 'Internal audit findings and follow-up evidence', 'Management reporting on control deficiencies'],
  // CC5 – Control Activities
  'CC5.1':  ['Control inventory mapped to risks', 'Evidence controls address identified risks to commitments', 'Risk treatment plan linked to control implementation'],
  'CC5.2':  ['Control selection rationale documentation', 'Evidence controls are designed effectively', 'Control testing results'],
  'CC5.3':  ['Security policies and procedures documentation', 'Evidence policies are communicated and acknowledged', 'Policy review and update records'],
  // CC6 – Logical and Physical Access
  'CC6.1':  ['Access control policy', 'Logical access configuration evidence (MFA, SSO)', 'Access request and approval records'],
  'CC6.2':  ['User provisioning and de-provisioning procedure', 'Onboarding and offboarding access records', 'User account lifecycle management evidence'],
  'CC6.3':  ['RBAC or least-privilege access model documentation', 'Periodic access review records', 'Evidence of privilege minimisation in access configuration'],
  'CC6.4':  ['Access control review records for new and changed systems', 'Evidence access rights are evaluated during change management', 'Change management records with access sign-off'],
  'CC6.5':  ['Termination access revocation records', 'System-generated account disable confirmation', 'Offboarding checklist with access removal evidence'],
  'CC6.6':  ['Vulnerability scanning and penetration testing reports', 'Evidence of threat monitoring for logical access systems', 'Intrusion detection or alerting configuration evidence'],
  'CC6.7':  ['Data loss prevention (DLP) policy and configuration', 'Encryption in transit configuration evidence', 'Evidence unauthorised data transmission is controlled'],
  'CC6.8':  ['Software installation policy', 'Application whitelisting or MDM configuration evidence', 'Evidence unauthorised software is detected and prevented'],
  // CC7 – System Operations
  'CC7.1':  ['Configuration management procedure', 'Configuration baseline documentation', 'Configuration review or deviation records'],
  'CC7.2':  ['Anomaly detection and alerting configuration', 'SIEM or log monitoring evidence', 'Alert triage records'],
  'CC7.3':  ['Incident triage and severity classification records', 'Incident assessment and response records', 'Evidence incidents are evaluated against commitments'],
  'CC7.4':  ['Incident response plan', 'Incident response and recovery records', 'Post-incident review reports'],
  'CC7.5':  ['Vulnerability disclosure policy', 'Vulnerability scanning reports', 'Evidence vulnerabilities are tracked and remediated'],
  // CC8 – Change Management
  'CC8.1':  ['Change management procedure', 'Change request and approval records', 'Change log with pre/post-implementation review evidence'],
  // CC9 – Risk Mitigation
  'CC9.1':  ['Business continuity plan', 'BCP test results', 'Evidence business disruption risks are managed'],
  'CC9.2':  ['Vendor risk management procedure', 'Supplier security assessment records', 'Contractual security requirements in third-party agreements'],
  // A1 – Availability
  'A1.1':   ['System availability SLA and monitoring evidence', 'Uptime and performance measurement records', 'Capacity planning documentation'],
  'A1.2':   ['Environmental threat protection controls documentation', 'Data centre physical security and environmental monitoring evidence', 'Redundancy and failover design documentation'],
  'A1.3':   ['Backup policy and configuration evidence', 'Backup restoration test results', 'Disaster recovery plan and DR test results'],
  // C1 – Confidentiality
  'C1.1':   ['Data classification policy identifying confidential information', 'Confidential data inventory or register', 'Access controls applied to confidential data'],
  'C1.2':   ['Secure disposal and data deletion procedure', 'Evidence confidential data is destroyed at end of life', 'Media sanitisation or destruction certificates'],
  // PI1 – Processing Integrity
  'PI1.1':  ['Input validation controls documentation', 'Evidence of data entry controls and validation rules', 'Sample records showing input validation in practice'],
  'PI1.2':  ['Processing monitoring and reconciliation controls', 'Evidence of processing accuracy monitoring', 'Exception and error monitoring records'],
  'PI1.3':  ['Output accuracy verification controls', 'Evidence output reports are reconciled or validated', 'Output review and approval records'],
  'PI1.4':  ['Error correction procedure', 'Error correction logs', 'Evidence errors are identified, corrected, and root-caused'],
  'PI1.5':  ['Data integrity monitoring controls', 'Database integrity checks or checksums configuration', 'Evidence stored data is protected from unauthorised modification'],
};

export const BSIC5_EVIDENCE_MAP: Record<string, string[]> = {
  // OIS – Organisation and Information Security
  'OIS-01': ['Information security policy approved by management', 'Policy review history', 'Evidence policy is communicated to all personnel'],
  'OIS-02': ['Information security policy', 'Policy publication and distribution records'],
  'OIS-03': ['Interfaces and dependencies documentation', 'Evidence cross-team security dependencies are identified and managed'],
  'OIS-04': ['Segregation of duties policy', 'Evidence incompatible duties are separated in role assignments'],
  'OIS-05': ['Contact list for relevant authorities and interest groups', 'Evidence of engagement with security communities and regulators'],
  'OIS-06': ['Risk management policy', 'Risk register and treatment plan', 'Evidence risk management feeds into business decisions'],
  'OIS-07': ['Risk management policy application records', 'Evidence risk management is applied consistently across the organisation'],
  // SP – Policies and Instructions
  'SP-01':  ['Policy and procedure documentation register', 'Evidence all security policies are documented and current'],
  'SP-02':  ['Policy review schedule and management approval records', 'Evidence policies are reviewed and approved regularly'],
  'SP-03':  ['Policy exception request and approval records', 'Evidence exceptions are risk-assessed and time-limited'],
  // AM – Asset Management
  'AM-01':  ['Asset register with ownership assignments', 'Evidence asset register is reviewed and maintained'],
  'AM-02':  ['Acceptable use and safe handling policy', 'Signed user acknowledgements', 'Evidence policy is communicated'],
  'AM-03':  ['Hardware commissioning procedure', 'New hardware approval and inventory records'],
  'AM-04':  ['Hardware decommissioning and disposal procedure', 'Evidence of certified disposal or data sanitisation'],
  'AM-05':  ['Commitment to permissible use documentation', 'Signed acceptable use agreements'],
  'AM-06':  ['Asset classification and labelling policy', 'Evidence assets are classified and labelled consistently'],
  // PS – Physical Security
  'PS-01':  ['Physical security policy and requirements', 'Physical security risk assessment'],
  'PS-02':  ['Redundancy model documentation', 'Evidence redundant systems and paths are in place'],
  'PS-03':  ['Perimeter protection controls documentation', 'Physical access control evidence (barriers, locks, etc.)'],
  'PS-04':  ['Physical site access control system evidence', 'Access badge records and secure area authorisation logs'],
  'PS-05':  ['Physical threat protection documentation (fire, flood, etc.)', 'Evidence environmental controls are in place'],
  'PS-06':  ['Power failure protection documentation (UPS, generators)', 'Evidence power resilience is tested'],
  'PS-07':  ['Operational and environmental monitoring records', 'Evidence temperature, humidity, and utility alerts are managed'],
  // CRY – Cryptography
  'CRY-01': ['Cryptography and key management policy', 'Encryption algorithm and key length standards documentation'],
  'CRY-02': ['Encryption in transit (TLS/SSL) configuration evidence', 'Certificate inventory and renewal tracking records'],
  'CRY-03': ['Encryption at rest configuration evidence for key data stores', 'Evidence encryption standards are applied consistently'],
  'CRY-04': ['Key management procedure', 'Key generation, storage, rotation, and revocation records'],
  // IDM – Identity and Access Management
  'IDM-01': ['User account management policy', 'Evidence access control policy is documented and approved'],
  'IDM-02': ['Access provisioning and change procedure', 'Access request and approval records'],
  'IDM-03': ['Account lockout policy configuration', 'Evidence lockout controls are enforced on inactivity and failed logins'],
  'IDM-04': ['Joiner/mover/leaver process documentation', 'Evidence access is adjusted when roles change'],
  'IDM-05': ['Access review schedule and completed review records', 'Evidence periodic access reviews are conducted and acted on'],
  'IDM-06': ['Privileged access management policy', 'Privileged account list and review records'],
  'IDM-07': ['Policy restricting admin access to cloud customer data', 'Evidence customer data access is logged and controlled'],
  'IDM-08': ['Credential protection policy', 'Evidence authentication information is stored and transmitted securely'],
  'IDM-09': ['Authentication policy', 'MFA configuration evidence', 'Evidence strong authentication is enforced'],
  // HR – Human Resources
  'HR-01':  ['Background screening policy', 'Evidence pre-employment screening is conducted for defined roles'],
  'HR-02':  ['Employment contracts with security obligations', 'Evidence staff sign security terms at onboarding'],
  'HR-03':  ['Security awareness training programme documentation', 'Training completion and curriculum records'],
  'HR-04':  ['Disciplinary policy for security violations', 'Evidence disciplinary process is documented and enforced'],
  'HR-05':  ['Termination and offboarding procedure', 'Evidence access rights and assets are returned on departure'],
  'HR-06':  ['Signed confidentiality/NDA agreements', 'Evidence all staff and contractors sign NDAs'],
  // COM – Compliance
  'COM-01': ['Regulatory obligations register', 'Evidence applicable legal and regulatory requirements are identified'],
  'COM-02': ['Audit planning policy', 'Internal audit schedule and completed reports'],
  'COM-03': ['ISMS internal audit programme', 'Internal audit findings tracker and resolution records'],
  'COM-04': ['Information security performance reporting', 'Management review meeting minutes and follow-up actions'],
  // OPS – Operations
  'OPS-01': ['Capacity management planning documentation', 'Capacity forecast and planning records'],
  'OPS-02': ['Capacity monitoring configuration and dashboards', 'Evidence capacity thresholds are monitored'],
  'OPS-03': ['Capacity resource control records', 'Evidence capacity issues are identified and resolved proactively'],
  'OPS-04': ['Anti-malware deployment and configuration evidence', 'Malware scan and detection logs'],
  'OPS-05': ['Anti-malware implementation records', 'Evidence anti-malware is deployed and updated across all endpoints'],
  'OPS-06': ['Backup (data protection and recovery) concept documentation', 'Backup policy and RTO/RPO targets'],
  'OPS-07': ['Backup monitoring records', 'Evidence backup jobs are monitored and failures are alerted'],
  'OPS-08': ['Backup restoration test records', 'Evidence backups are regularly restored and validated'],
  'OPS-09': ['Backup storage architecture documentation', 'Evidence backups are stored securely and geographically separated'],
  'OPS-10': ['Logging and monitoring strategy document', 'Evidence a comprehensive monitoring concept is documented'],
  'OPS-11': ['Logging metadata management policy', 'Evidence log metadata integrity is protected'],
  'OPS-12': ['Log access control configuration', 'Evidence access to logs is restricted to authorised personnel'],
  'OPS-13': ['Security event detection rules and configuration', 'Evidence relevant security events are identified and logged'],
  'OPS-14': ['Log retention policy', 'Evidence logs are stored for required periods and protected from tampering'],
  'OPS-15': ['Evidence user actions can be traced via logs', 'Non-repudiation and accountability controls documentation'],
  'OPS-16': ['Monitoring system configuration documentation', 'Evidence monitoring tools are correctly and comprehensively configured'],
  'OPS-17': ['Monitoring system availability SLA or uptime evidence', 'Evidence monitoring software is maintained and available'],
  'OPS-18': ['Vulnerability management procedure', 'Evidence vulnerabilities are identified and tracked'],
  'OPS-19': ['Vulnerability remediation records', 'Evidence vulnerabilities are resolved within agreed timeframes'],
  'OPS-20': ['Vulnerability scanning tool configuration and results', 'Evidence regular vulnerability scans are performed'],
  'OPS-21': ['Customer incident notification policy', 'Evidence cloud customers are informed of relevant security incidents'],
  'OPS-22': ['Vulnerability testing and documentation records', 'Penetration test results and disclosure records'],
  'OPS-23': ['Vulnerability prioritisation and risk-rating records', 'Evidence vulnerabilities are ranked and addressed by severity'],
  'OPS-24': ['Multi-tenancy data segregation architecture', 'Evidence customer datasets are logically separated in shared infrastructure'],
  // BCM – Business Continuity Management
  'BCM-01': ['Business continuity policy and management responsibility', 'BCM governance and programme documentation'],
  'BCM-02': ['Business impact analysis (BIA) records', 'Evidence critical functions and RTOs are defined'],
  'BCM-03': ['Business continuity plan (BCP)', 'BCP test results and evidence of regular testing'],
  'BCM-04': ['BCP and DR test and verification records', 'Evidence plans are reviewed and improved after tests'],
  // SIM – Security Incident Management
  'SIM-01': ['Incident management policy', 'Incident response roles and responsibilities'],
  'SIM-02': ['Incident response and processing records', 'Evidence security incidents are handled per the defined process'],
  'SIM-03': ['Incident documentation and reporting procedure', 'Incident log and severity classification records'],
  'SIM-04': ['User incident reporting channels', 'Evidence all staff know how to report security events'],
  'SIM-05': ['Incident evaluation and lessons learned process', 'Post-incident review reports and corrective actions'],
  // SSO – Supplier and Third Party Management
  'SSO-01': ['Supplier security policy', 'Supplier risk assessment records', 'Supplier security due diligence process'],
  'SSO-02': ['Risk assessment of service providers', 'Supplier security assessment results and ratings'],
  'SSO-03': ['Directory of service providers and suppliers', 'Evidence critical supplier inventory is maintained'],
  'SSO-04': ['Supplier compliance monitoring records', 'Evidence supplier obligations are regularly reviewed'],
  'SSO-05': ['ICT third-party risk management procedure', 'Evidence ICT supplier dependencies are understood and managed'],
  // COS – Communication and Network Security
  'COS-01': ['Technical network security controls documentation', 'Firewall and network security configuration evidence'],
  'COS-02': ['Network connection security requirements', 'Evidence security requirements are enforced for all connections to the CSP network'],
  'COS-03': ['Network connection monitoring configuration', 'Network traffic monitoring and anomaly detection records'],
  'COS-04': ['Cross-network access policy', 'Evidence cross-network access is restricted, authorised, and monitored'],
  'COS-05': ['Dedicated administration network documentation', 'Evidence admin interfaces are isolated from production networks'],
  'COS-06': ['Data traffic segregation architecture', 'VLAN or network segmentation configuration evidence for multi-tenant environments'],
  'COS-07': ['Current network topology diagrams', 'Evidence network topology documentation is maintained and reviewed'],
  'COS-08': ['Data transmission security policy', 'Evidence encryption is enforced for data in transit between systems'],
  // DEV – Development and Change Management
  'DEV-01': ['Secure development and procurement policy', 'Evidence secure coding standards are adopted and communicated'],
  'DEV-02': ['Outsourced development security requirements', 'Third-party development contracts with security clauses'],
  'DEV-03': ['Change management policy', 'Change request and approval records'],
  'DEV-04': ['Developer security training records', 'Secure development awareness programme evidence'],
  'DEV-05': ['Development risk assessment records', 'Evidence security risks are assessed before deployment'],
  'DEV-06': ['Test plan and test results documentation', 'Evidence changes are tested before deployment to production'],
  'DEV-07': ['Change log records', 'Evidence code and configuration changes are tracked and auditable'],
  'DEV-08': ['Version control system configuration', 'Evidence all code is managed in a version control system'],
  'DEV-09': ['Deployment approval records', 'Evidence production deployments are authorised before release'],
  'DEV-10': ['Environment separation architecture', 'Evidence dev, test, and production environments are isolated'],
  // PI – Portability and Interfaces
  'PI-01':  ['Interface and API security documentation', 'Input/output interface security requirements and validation controls'],
  'PI-02':  ['Data provision contractual agreements', 'Evidence contracts include data security and handling requirements'],
  'PI-03':  ['Secure deletion procedure', 'Evidence customer data is securely deleted on request or at contract end'],
  // PSS – Cloud Service Provider Security
  'PSS-01': ['Published cloud customer security guidelines', 'Evidence security guidance is available to cloud customers'],
  'PSS-02': ['Cloud service vulnerability identification procedure', 'Evidence cloud service-specific vulnerabilities are tracked'],
  'PSS-03': ['Online register of known vulnerabilities or CVE tracking records', 'Evidence known vulnerabilities are disclosed and tracked'],
  'PSS-04': ['Error handling and logging policy', 'Evidence error messages do not expose sensitive information'],
  'PSS-05': ['Authentication security configuration', 'Evidence strong authentication is enforced for cloud service access'],
  'PSS-06': ['Session management configuration', 'Evidence sessions are protected and expire appropriately'],
  'PSS-07': ['Credential protection controls', 'Evidence authentication credentials are stored and transmitted securely'],
  'PSS-08': ['Role-based access control (RBAC) documentation', 'Evidence least-privilege principle is applied in the cloud service'],
  'PSS-09': ['Authorisation controls configuration', 'Evidence access decisions are enforced at all service levels'],
  'PSS-10': ['Software Defined Networking security controls documentation', 'Evidence SDN is configured to enforce network isolation'],
  'PSS-12': ['Data processing and storage location policy', 'Evidence data locations are documented and meet regulatory requirements'],
  // INQ – Investigative Inquiries
  'INQ-01': ['Legal request handling procedure', 'Evidence requests are legally assessed before any disclosure'],
  'INQ-02': ['Customer notification procedure for investigation requests', 'Evidence customers are informed where legally permitted'],
  'INQ-03': ['Data access policy for investigation requests', 'Evidence conditions for access or disclosure are documented'],
  'INQ-04': ['Minimum disclosure principle documentation', 'Evidence only required data is disclosed in response to authority requests'],
};


export const NIS2_EVIDENCE_MAP: Record<string, string[]> = {
  // Article 21 – Cybersecurity risk-management measures
  'ART21-A':  ['Risk analysis and information system security policy', 'Periodic risk assessment records', 'Evidence security policy addresses identified risks'],
  'ART21-B':  ['Incident response plan', 'Incident detection and response records', 'Post-incident review reports'],
  'ART21-C':  ['Business continuity plan', 'Backup management and restoration test evidence', 'Crisis management and disaster recovery plan'],
  'ART21-D':  ['Supply chain security policy', 'Supplier security assessment records', 'Contractual security requirements for suppliers and service providers'],
  'ART21-E':  ['Network and systems security policy', 'Vulnerability management procedure', 'Vulnerability scanning and remediation records'],
  'ART21-F':  ['Security effectiveness assessment procedure', 'Evidence of regular security testing and audit activities', 'Documented security review and improvement plan'],
  'ART21-G':  ['Cyber hygiene policy and baseline security practices', 'Security awareness and training programme documentation', 'Training completion records'],
  'ART21-H':  ['Cryptography and encryption policy', 'Encryption configuration evidence for systems and data in scope', 'Key management procedure'],
  'ART21-I':  ['Human resources security policy', 'Access control policy', 'Asset management policy and register'],
  'ART21-J':  ['Multi-factor authentication policy', 'MFA configuration evidence for all relevant systems', 'Evidence MFA is enforced for privileged and remote access'],
  // Article 23 – Reporting obligations
  'ART23':    ['Incident notification procedure and timeline obligations documentation', 'Evidence of timely initial notifications to competent authority (within 24 hours)', 'Evidence of final incident reports submitted within required deadline'],
};

const formatDate = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_TONE: Record<string, Tone> = { active: 'success', draft: 'warning' };

export const ControlMatricesList: React.FC = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const { data: matrices = [], isLoading, error } = useQuery<any[]>({
    queryKey: ['controlMatrices'],
    queryFn: controlMatrixApi.getAll,
  });

  const { data: departments = [] } = useQuery<any[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const { data: frameworks = [] } = useQuery<any[]>({
    queryKey: ['adminFrameworks'],
    queryFn: adminFrameworksApi.getAll,
  });

  const frameworksGrouped = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (frameworks || []).forEach((fw: any) => {
      const category = fw.category || 'Other';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(fw);
    });
    Object.keys(grouped).forEach((c) => {
      grouped[c].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
    return grouped;
  }, [frameworks]);

  const { data: evidenceRegisters = [] } = useQuery<any[]>({
    queryKey: ['evidenceRegisters'],
    queryFn: evidenceRegistersApi.getAll,
  });

  const [showMatrixModal, setShowMatrixModal] = useState(false);

  const [currentMatrixId, setCurrentMatrixId] = useState<string | null>(null);
  const [matrixName, setMatrixName] = useState('');
  const [matrixDescription, setMatrixDescription] = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [customSelected, setCustomSelected] = useState(false);
  const [matrixStatus, setMatrixStatus] = useState<'active' | 'draft' | 'archived'>('active');
  const [generateEvidenceOnCreate, setGenerateEvidenceOnCreate] = useState(false);
  const [createEvidenceRegisterChoice, setCreateEvidenceRegisterChoice] = useState('__new__');

  type SortField = 'name' | 'controlCount' | 'status' | 'createdAt';
  const { sorted: sortedMatrices, field: sortField, dir: sortDir, toggle: handleSort } = useSortableData<any, SortField>(
    matrices,
    (m, field) => (field === 'controlCount' ? (m.controlCount ?? 0) : (m[field] ?? '').toString().toLowerCase()),
    'name',
  );
  const { pageItems: pageMatrices, page: matrixPage, setPage: setMatrixPage, totalPages: matrixTotalPages, pageSize: matrixPageSize, totalItems: matrixTotalItems } = usePagination(sortedMatrices, 25);

  const openCreateModal = () => {
    setCurrentMatrixId(null);
    setMatrixName('');
    setMatrixDescription('');
    setDepartmentId('');
    setSelectedFrameworks([]);
    setCustomSelected(false);
    setMatrixStatus('active');
    setGenerateEvidenceOnCreate(false);
    setCreateEvidenceRegisterChoice('__new__');
    setShowMatrixModal(true);
  };

  const openEditModal = (m: any) => {
    setCurrentMatrixId(m.matrixId);
    setMatrixName(m.name || '');
    setMatrixDescription(m.description || '');
    setDepartmentId(m.departmentId || '');
    setSelectedFrameworks(Array.isArray(m.frameworks) ? m.frameworks : []);
    setCustomSelected(false);
    setMatrixStatus((m.status as any) || 'active');
    setGenerateEvidenceOnCreate(false);
    setShowMatrixModal(true);
  };

  const createOrUpdate = useMutation({
    mutationFn: async () => {
      const selectedDept = departments.find((d: any) => d.departmentId === departmentId);

      const matrixData = {
        name: matrixName,
        description: matrixDescription,
        departmentId: departmentId || null,
        departmentName: departmentId ? selectedDept?.name || null : null,
        frameworks: selectedFrameworks,
        status: matrixStatus,
      };

      const isUpdate = currentMatrixId !== null;
      if (isUpdate) {
        return controlMatrixApi.update(currentMatrixId, matrixData);
      }

      const created = await controlMatrixApi.create(matrixData);
      const createdId = (created as any)?.matrixId;
      if (createdId) {
        try {
          await configApi.applyMatrixTemplate(createdId, 'standard');
        } catch (err) {
          console.warn('Failed to apply standard template', err);
        }

        let evidenceCount: number | null = null;
        if (generateEvidenceOnCreate && selectedFrameworks.length > 0) {
          try {
            const matched = getFrameworksForMatrix({ frameworks: selectedFrameworks });
            const frameworkCodes = matched.map((f) => f.code);
            if (frameworkCodes.length > 0) {
              const evidenceMap = await evidenceTemplatesApi.getByFrameworks(frameworkCodes);
              evidenceCount = await generateEvidenceForMatrix({
                matrixId: createdId,
                matrixName,
                frameworkCodes,
                frameworkIds: matched.map((f) => f.frameworkId),
                frameworkLabel: matched.map((f) => f.label).join(' + ') || 'Framework',
                registerChoice: createEvidenceRegisterChoice,
                evidenceMap,
              });
            }
          } catch (err) {
            console.warn('Failed to generate evidence for new matrix', err);
            toast.warning('Matrix created, but evidence generation failed — you can try again from the matrix list.');
          }
        }

        return { ...(created as any), evidenceCount };
      }

      return created;
    },
    onSuccess: async (result: any) => {
      await queryClient.invalidateQueries({ queryKey: ['controlMatrices'] });
      await queryClient.invalidateQueries({ queryKey: ['controls-soa'] });
      await queryClient.invalidateQueries({ queryKey: ['evidenceRegisters'] });
      if (currentMatrixId) {
        await queryClient.invalidateQueries({ queryKey: ['controls', currentMatrixId] });
      }
      setShowMatrixModal(false);
      if (currentMatrixId) {
        if (result?.controlsAdded) {
          toast.success(`Matrix updated. ${result.controlsAdded} controls added from new frameworks.`);
        } else {
          toast.success('Matrix updated.');
        }
      } else if (typeof result?.evidenceCount === 'number') {
        toast.success(`Matrix created with standard fields. ${result.evidenceCount} evidence items generated and linked.`);
      } else {
        toast.success('Matrix created, with standard fields.');
      }
    },
    onError: (err: any) => {
      console.error(err);
      toast.error('Failed to save control matrix. Please try again.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (matrixId: string) => {
      await controlMatrixApi.delete(matrixId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['controlMatrices'] });
      await queryClient.invalidateQueries({ queryKey: ['controls-soa'] });
      toast.success('Matrix deleted.');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error('Failed to delete control matrix. Please try again.');
    },
  });

  const canSave = useMemo(() => {
    if (!matrixName.trim()) return false;
    if (selectedFrameworks.length === 0 && !customSelected) return false;
    return true;
  }, [matrixName, selectedFrameworks, customSelected]);

  const saveMatrix = () => {
    if (!matrixName.trim()) {
      toast.warning('Matrix Name is required');
      return;
    }
    if (selectedFrameworks.length === 0 && !customSelected) {
      toast.warning('Please select at least one framework or choose Custom to add controls manually');
      return;
    }
    createOrUpdate.mutate();
  };

  const askDelete = async (m: any) => {
    const ok = await swal.confirm(
      `Delete "${m.name || m.matrixId}"?`,
      'This will also delete all controls in this matrix. This action cannot be undone.',
    );
    if (!ok) return;
    deleteMutation.mutate(m.matrixId);
  };

  // ── Generate evidence for an existing matrix ────────────────────────────
  const [showGenEvidenceModal, setShowGenEvidenceModal] = useState(false);
  const [genEvidenceMatrixId, setGenEvidenceMatrixId] = useState('');
  const [genEvidenceMatrixName, setGenEvidenceMatrixName] = useState('');
  const [genEvidenceRegisterId, setGenEvidenceRegisterId] = useState('');
  const [genEvidenceFrameworkCodes, setGenEvidenceFrameworkCodes] = useState<string[]>([]);
  const [genEvidenceFrameworkIds, setGenEvidenceFrameworkIds] = useState<string[]>([]);
  const [genEvidenceFrameworkLabel, setGenEvidenceFrameworkLabel] = useState('');

  // Fetch evidence templates from DynamoDB via API whenever the modal is open
  const { data: genEvidenceMap = {}, isLoading: genEvidenceTemplatesLoading } = useQuery<Record<string, string[]>>({
    queryKey: ['evidenceTemplates', genEvidenceFrameworkCodes.join(',')],
    queryFn: () => evidenceTemplatesApi.getByFrameworks(genEvidenceFrameworkCodes),
    enabled: showGenEvidenceModal && genEvidenceFrameworkCodes.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Returns matching frameworks (real frameworkId + a display code/label) for a matrix.
  // frameworkId used to be looked up here and then discarded, keeping only a hardcoded
  // name-substring-matched code/label string - which is how generated evidence ended up with
  // an evidence.fields.frameworkCodes display tag but no real Linked Artefacts connection to
  // the actual framework record (see generateEvidenceForMatrix below, which now uses this
  // frameworkId to create that real link). The code/label strings stay as the display tag
  // written into frameworkCodes for backward compatibility with evidence generated before
  // this fix - only the real link is new.
  const getFrameworksForMatrix = (m: any): Array<{ frameworkId: string; code: string; label: string }> => {
    const mFrameworks: string[] = Array.isArray(m.frameworks) ? m.frameworks : [];
    const matches: Array<{ frameworkId: string; code: string; label: string }> = [];
    for (const id of mFrameworks) {
      const fw = (frameworks as any[]).find((f: any) => f.frameworkId === id);
      const name = (fw?.name ?? '').toLowerCase();
      if (name.includes('27001')) matches.push({ frameworkId: id, code: 'ISO 27001', label: 'ISO 27001' });
      else if (name.includes('soc 2') || name.includes('soc2')) matches.push({ frameworkId: id, code: 'SOC 2', label: 'SOC 2' });
      else if (name.includes('bsi') || name.includes('c5')) matches.push({ frameworkId: id, code: 'BSI C5', label: 'BSI C5' });
      else if (name.includes('nis 2') || name.includes('nis2')) matches.push({ frameworkId: id, code: 'NIS 2', label: 'NIS 2' });
    }
    return matches;
  };

  const openGenEvidence = (m: any) => {
    const matchedFrameworks = getFrameworksForMatrix(m);
    setGenEvidenceFrameworkCodes(matchedFrameworks.map((f) => f.code));
    setGenEvidenceFrameworkIds(matchedFrameworks.map((f) => f.frameworkId));
    setGenEvidenceFrameworkLabel(matchedFrameworks.map((f) => f.label).join(' + ') || 'Framework');
    setGenEvidenceMatrixId(m.matrixId);
    setGenEvidenceMatrixName(m.name || m.matrixId);
    setGenEvidenceRegisterId('');
    setShowGenEvidenceModal(true);
  };

  // Core evidence-generation routine, shared by the standalone "Generate Evidence"
  // modal and the "Also generate evidence" option in the Create modal.
  const generateEvidenceForMatrix = async (params: {
    matrixId: string;
    matrixName: string;
    frameworkCodes: string[];
    // Real frameworkId(s) - same frameworks frameworkCodes is a display-only string tag for.
    // Used to create a real Linked Artefacts evidence<->framework link (RUB-50374) for every
    // item this function creates, so the Frameworks column and the actual link both agree.
    frameworkIds: string[];
    frameworkLabel: string;
    registerChoice: string; // registerId, or '__new__' to create one named after the matrix
    evidenceMap: Record<string, string[]>;
  }): Promise<number> => {
    const { matrixId, matrixName, frameworkCodes, frameworkIds, frameworkLabel, registerChoice, evidenceMap } = params;
    if (!registerChoice) throw new Error('No evidence register selected');

    // Resolve or create the evidence register
    let resolvedRegisterId: string;
    if (registerChoice === '__new__') {
      const newReg = await evidenceRegistersApi.create({ name: matrixName, status: 'active' });
      resolvedRegisterId = newReg?.registerId || newReg?.register?.registerId || '';
      if (!resolvedRegisterId) throw new Error('Failed to obtain evidence register ID');
      // Apply the standard evidence template so all fields are available
      try {
        const evidenceTemplates = await templateApi.list('evidence');
        const activeTemplate = (evidenceTemplates as any[])
          .filter((t: any) => t.isActive)
          .sort((a: any, b: any) => (a.displayName || '').localeCompare(b.displayName || ''))[0];
        const templateName = activeTemplate?.templateName || 'standard';
        await registerConfigApi.applyTemplate('evidence', resolvedRegisterId, templateName);
      } catch (e) {
        console.warn('Could not apply evidence template to new register:', e);
      }
    } else {
      resolvedRegisterId = registerChoice;
    }
    if (!resolvedRegisterId) throw new Error('Failed to obtain evidence register ID');

    // Fetch controls for this matrix
    const controlsRes = await api.get<{ controls: any[] }>(`/controls?matrixId=${encodeURIComponent(matrixId)}`);
    const controls = (controlsRes as any).controls || [];

    // Build title → [controlId, ...] map so shared evidence is created only once
    const titleToControls = new Map<string, { internalId: string; userFacingId: string }[]>();
    for (const control of controls) {
      const isoId: string = control.fields?.control_id || control.controlId || '';
      const userFacingId: string = control.fields?.control_id || '';
      const titles: string[] | undefined = evidenceMap[isoId];
      if (!titles?.length || !control.controlId) continue;
      for (const title of titles) {
        if (!titleToControls.has(title)) titleToControls.set(title, []);
        titleToControls.get(title)!.push({ internalId: control.controlId, userFacingId });
      }
    }

    if (titleToControls.size === 0) throw new Error(`No matching ${frameworkLabel} evidence found for controls in this matrix`);

    // Create each unique evidence item exactly once
    const titleToEvidenceId = new Map<string, string>();
    await Promise.allSettled(
      Array.from(titleToControls.keys()).map(async (title) => {
        const ctrlEntries = titleToControls.get(title) || [];
        const controlUserIds = [...new Set(ctrlEntries.map((c) => c.userFacingId).filter(Boolean))].sort();
        const created = await evidenceApi.create({
          registerId: resolvedRegisterId,
          fields: {
            evidence_title: title,
            evidence_status: 'Active',
            frameworkCodes,
            controlUserIds,
          },
        });
        const ev = created?.evidence || created;
        const evidenceId = ev?.evidenceId || created?.evidenceId;
        if (evidenceId) titleToEvidenceId.set(title, evidenceId);
      })
    );

    // Build controlId → [evidenceId, ...] so each control gets all its linked items in one call
    const controlToEvidenceIds = new Map<string, string[]>();
    for (const [title, ctrlEntries] of titleToControls.entries()) {
      const evidenceId = titleToEvidenceId.get(title);
      if (!evidenceId) continue;
      for (const { internalId } of ctrlEntries) {
        if (!controlToEvidenceIds.has(internalId)) controlToEvidenceIds.set(internalId, []);
        controlToEvidenceIds.get(internalId)!.push(evidenceId);
      }
    }

    // Link all evidence to each control in a single API call per control
    await Promise.allSettled(
      Array.from(controlToEvidenceIds.entries()).map(([controlId, evidenceIds]) =>
        linkedArtefactsApi
          .createLinks({
            source_id: controlId,
            source_type: 'control',
            source_register: matrixId,
            target_artefacts: evidenceIds.map((id) => ({
              id,
              type: 'evidence',
              register: resolvedRegisterId,
            })),
          })
          .catch((err: any) => console.warn(`Failed to link evidence to ${controlId}`, err))
      )
    );

    // Link every newly created evidence item to its real framework(s) via Linked Artefacts
    // (RUB-50374) - this is what the Frameworks column should actually be backed by, not just
    // the frameworkCodes display string written into `fields` above. One call per framework
    // covers every evidence item created in this run, same batching shape as the control-link
    // block above.
    const allNewEvidenceIds = Array.from(titleToEvidenceId.values());
    if (allNewEvidenceIds.length > 0 && frameworkIds.length > 0) {
      await Promise.allSettled(
        frameworkIds.map((frameworkId) =>
          linkedArtefactsApi
            .createLinks({
              source_id: frameworkId,
              source_type: 'framework',
              source_register: 'frameworks',
              target_artefacts: allNewEvidenceIds.map((id) => ({
                id,
                type: 'evidence',
                register: resolvedRegisterId,
              })),
            })
            .catch((err: any) => console.warn(`Failed to link evidence to framework ${frameworkId}`, err))
        )
      );
    }

    // Silently sync RACI from each control to its newly linked evidence items
    try {
      const controlIds = Array.from(controlToEvidenceIds.keys());
      const controlRaciResults = await Promise.allSettled(
        controlIds.map(async (controlId) => {
          const raci = await raciApi.getByArtefact('control', controlId);
          return { controlId, raci };
        })
      );

      const seenKeys = new Set<string>();
      const batchItems: Array<{ artefactType: string; artefactId: string; personId: string; role: string }> = [];

      for (const result of controlRaciResults) {
        if (result.status !== 'fulfilled' || !result.value.raci.length) continue;
        const { controlId, raci } = result.value;
        const evidenceIds = controlToEvidenceIds.get(controlId) || [];
        for (const evidenceId of evidenceIds) {
          for (const r of raci) {
            const k = `${evidenceId}:${r.personId}:${r.role}`;
            if (!seenKeys.has(k)) {
              seenKeys.add(k);
              batchItems.push({ artefactType: 'evidence', artefactId: evidenceId, personId: r.personId, role: r.role });
            }
          }
        }
      }

      if (batchItems.length > 0) {
        await raciApi.batchCreate(batchItems);
      }
    } catch (e) {
      console.warn('RACI sync after evidence generation failed silently:', e);
    }

    return titleToEvidenceId.size;
  };

  const generateEvidenceMutation = useMutation({
    mutationFn: () =>
      generateEvidenceForMatrix({
        matrixId: genEvidenceMatrixId,
        matrixName: genEvidenceMatrixName,
        frameworkCodes: genEvidenceFrameworkCodes,
        frameworkIds: genEvidenceFrameworkIds,
        frameworkLabel: genEvidenceFrameworkLabel,
        registerChoice: genEvidenceRegisterId,
        evidenceMap: genEvidenceMap,
      }),
    onSuccess: (count) => {
      setShowGenEvidenceModal(false);
      toast.success(`${count} evidence items generated and linked to controls.`);
    },
    onError: (err: any) => {
      console.error(err);
      toast.error('Failed to generate evidence. Please try again.');
    },
  });

  const toggleFramework = (frameworkId: string, checked: boolean) => {
    setSelectedFrameworks((prev) => {
      const next = new Set(prev);
      if (checked) next.add(frameworkId);
      else next.delete(frameworkId);
      return Array.from(next);
    });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <PageHeader
        icon="ri-shield-check-line"
        title="Control Matrices"
        colour="primary"
        actions={
          <>
            <button className="np-btn np-btn-primary" onClick={openCreateModal}>
              <i className="ri-add-line me-1"></i>Create / Manage
            </button>
            <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
              <i className="ri-arrow-left-line me-1"></i>Back
            </button>
          </>
        }
      />

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0"><i className="ri-error-warning-line"></i>Failed to load control matrices.</div>
          ) : matrices.length === 0 ? (
            <div className="np-sub">No control matrices found.</div>
          ) : (
            <NimbusTable thead={<tr>
                    <SortableTh field="name" active={sortField === 'name'} dir={sortDir} onSort={handleSort}>Name</SortableTh>
                    <SortableTh field="controlCount" active={sortField === 'controlCount'} dir={sortDir} onSort={handleSort} width="140px">Controls</SortableTh>
                    <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={handleSort} width="140px">Status</SortableTh>
                    <SortableTh field="createdAt" active={sortField === 'createdAt'} dir={sortDir} onSort={handleSort} width="160px">Created</SortableTh>
                    <th style={{ width: '280px' }}></th>
                  </tr>}>
                  {pageMatrices.map((m: any) => (
                    <tr key={m.matrixId}>
                      <td>
                        <div className="np-row-title">{m.name || m.matrixId}</div>
                        {m.description ? <div className="np-sub">{m.description}</div> : null}
                      </td>
                      <td className="np-tabular">{m.controlCount || 0}</td>
                      <td>
                        <span
                          className={`np-chip np-tone-${toneFromMap(STATUS_TONE, m.status, 'neutral')}`}
                        >
                          {m.status || 'active'}
                        </span>
                      </td>
                      <td className="np-tabular">{formatDate(m.createdAt)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Link
                            className="np-icon-btn"
                            to={`/controls?matrixId=${encodeURIComponent(m.matrixId)}`}
                            title="View Controls"
                          >
                            <i className="ri-eye-line"></i>
                          </Link>
                          <Link
                            className="np-icon-btn"
                            to={`/control-config?matrixId=${encodeURIComponent(m.matrixId)}`}
                            title="Configure Fields"
                          >
                            <i className="ri-settings-3-line"></i>
                          </Link>
                          <button
                            className="np-icon-btn"
                            onClick={() => openEditModal(m)}
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          {getFrameworksForMatrix(m).length > 0 && (
                            <button
                              className="np-icon-btn np-icon-btn-success"
                              onClick={() => openGenEvidence(m)}
                              title={`Generate ${getFrameworksForMatrix(m).map((f) => f.label).join(' + ')} Evidence`}
                            >
                              <i className="ri-file-add-line"></i>
                            </button>
                          )}
                          <button
                            className="np-icon-btn np-icon-btn-danger"
                            onClick={() => askDelete(m)}
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </NimbusTable>
          )}
        </div>
        {matrices.length > 0 && (
          <Pagination page={matrixPage} totalPages={matrixTotalPages} pageSize={matrixPageSize} totalItems={matrixTotalItems} onPageChange={setMatrixPage} />
        )}
      </div>

      {showMatrixModal && (
        <NimbusModal size="lg">
          <NimbusModalContent
            title={currentMatrixId ? 'Edit Control Matrix' : 'Create Control Matrix'}
            onClose={() => setShowMatrixModal(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowMatrixModal(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={saveMatrix} disabled={!canSave || createOrUpdate.isPending}>
                  {createOrUpdate.isPending ? 'Saving…' : (currentMatrixId ? 'Save' : 'Create Matrix')}
                  {createOrUpdate.isPending ? (
                    <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                  ) : null}
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>
                    Matrix Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="np-input"
                    value={matrixName}
                    onChange={(e) => setMatrixName(e.target.value)}
                    placeholder="e.g., ISO 27001 Corporate, SOC 2 Product Security"
                  />
                </div>

                <div className="np-field">
                  <label>Description</label>
                  <textarea
                    className="np-textarea"
                    rows={3}
                    value={matrixDescription}
                    onChange={(e) => setMatrixDescription(e.target.value)}
                    placeholder="Describe the purpose of this control matrix..."
                  />
                </div>

                <div className="np-field">
                  <label>Department</label>
                  <select
                    className="np-select"
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {departments
                      .slice()
                      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                      .map((d: any) => (
                        <option key={d.departmentId} value={d.departmentId}>
                          {d.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="np-field">
                  <label>
                    Frameworks <span className="text-danger">*</span>
                  </label>
                  <p className="np-hint mb-2">
                    {currentMatrixId
                      ? 'Select additional frameworks to add their controls to this matrix'
                      : 'Select the frameworks whose controls should populate this matrix'}
                  </p>

                  {Object.keys(frameworksGrouped).length === 0 ? (
                    <div className="np-banner np-tone-warning mb-0">
                      <i className="ri-error-warning-line"></i>
                      Unable to load frameworks. Please refresh the page.
                    </div>
                  ) : (
                    <div className="border rounded" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <div className="p-3">
                        {Object.keys(frameworksGrouped)
                          .sort()
                          .map((category, i) => (
                            <div key={category} className={i > 0 ? 'mt-3' : ''}>
                              <h6 className="fw-semibold text-uppercase np-hint mb-2">{category}</h6>
                              <div className="list-group">
                                {frameworksGrouped[category].map((fw: any) => {
                                  const displayName = fw.version ? `${fw.name} ${fw.version}` : fw.name;
                                  const inputId = `fw_${String(fw.frameworkId).replace(/[^a-zA-Z0-9]/g, '_')}`;
                                  return (
                                    <label key={fw.frameworkId} htmlFor={inputId} className="list-group-item d-flex gap-2 py-2">
                                      <input
                                        className="form-check-input flex-shrink-0 mt-1"
                                        type="checkbox"
                                        id={inputId}
                                        checked={selectedFrameworks.includes(fw.frameworkId)}
                                        onChange={(e) => toggleFramework(fw.frameworkId, e.target.checked)}
                                      />
                                      <span>
                                        {displayName}
                                        {fw.description ? (
                                          <small className="np-hint d-block">{fw.description}</small>
                                        ) : null}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                      </div>
                      <div className="border-top p-3">
                        <label htmlFor="fw_custom" className="d-flex gap-2 mb-0">
                          <input
                            className="form-check-input flex-shrink-0 mt-1"
                            type="checkbox"
                            id="fw_custom"
                            checked={customSelected}
                            onChange={(e) => setCustomSelected(e.target.checked)}
                          />
                          <span>
                            Custom
                            <small className="np-hint d-block">No framework — I'll add controls manually</small>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {!currentMatrixId && selectedFrameworks.length > 0 && (
                  <div className="np-field">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="generateEvidenceOnCreate"
                        checked={generateEvidenceOnCreate}
                        onChange={(e) => setGenerateEvidenceOnCreate(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="generateEvidenceOnCreate">
                        Also generate evidence for these controls
                      </label>
                    </div>
                    {generateEvidenceOnCreate && (
                      <div className="np-field mt-2 ps-1">
                        <label>Evidence Register</label>
                        <select
                          className="np-select"
                          value={createEvidenceRegisterChoice}
                          onChange={(e) => setCreateEvidenceRegisterChoice(e.target.value)}
                        >
                          <option value="__new__">
                            + Create new register{matrixName ? ` (named "${matrixName}")` : ''}
                          </option>
                          {evidenceRegisters.map((r: any) => (
                            <option key={r.registerId} value={r.registerId}>
                              {r.name || r.registerId}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <div className="np-field">
                  <label>Status</label>
                  <select
                    className="np-select"
                    value={matrixStatus}
                    onChange={(e) => setMatrixStatus(e.target.value as any)}
                  >
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {showGenEvidenceModal && (
        <NimbusModal>
          <NimbusModalContent
            title={<><i className="ri-file-add-line me-2 text-success"></i>Generate {genEvidenceFrameworkLabel} Evidence</>}
            onClose={() => setShowGenEvidenceModal(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowGenEvidenceModal(false)} disabled={generateEvidenceMutation.isPending}>
                  Cancel
                </button>
                <button
                  className="np-btn np-btn-success"
                  onClick={() => generateEvidenceMutation.mutate()}
                  disabled={!genEvidenceRegisterId || generateEvidenceMutation.isPending || genEvidenceTemplatesLoading}
                >
                  {generateEvidenceMutation.isPending ? 'Generating evidence…' : 'Generate Evidence'}
                  {generateEvidenceMutation.isPending ? (
                    <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                  ) : null}
                </button>
              </>
            }
          >
                <p className="mb-1">
                  Matrix: <strong>{genEvidenceMatrixName}</strong>
                </p>
                {genEvidenceTemplatesLoading && (
                  <div className="d-flex align-items-center np-sub small mb-3">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading evidence templates…
                  </div>
                )}
                {!genEvidenceTemplatesLoading && (
                  <p className="np-sub small mb-3">
                    This will create {Object.keys(genEvidenceMap).length > 0 ? `up to ${Object.values(genEvidenceMap).flat().length}` : 'pre-mapped'} evidence items for <strong>{genEvidenceFrameworkLabel}</strong> and link each one to its corresponding control as a linked artefact.
                    Controls that have already had evidence generated will receive additional items.
                  </p>
                )}
                <div className="np-field np-mb-0">
                  <label>
                    Evidence Register <span className="text-danger">*</span>
                  </label>
                  <select
                    className="np-select"
                    value={genEvidenceRegisterId}
                    onChange={(e) => setGenEvidenceRegisterId(e.target.value)}
                    disabled={generateEvidenceMutation.isPending}
                  >
                    <option value="">— Select a register —</option>
                    <option value="__new__">+ Create new register (named "{genEvidenceMatrixName}")</option>
                    {(evidenceRegisters as any[])
                      .slice()
                      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                      .map((r: any) => (
                        <option key={r.registerId} value={r.registerId}>{r.name}</option>
                      ))}
                  </select>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
